/* eslint-disable no-else-return */
// @flow

import React from 'react';
import DateTimeColumn from 'speed-js-react-web/src/components/grommet2/tables/columns/DateTimeColumn';
import { Button, Box } from 'grommet';
import { closePanel } from 'esm-web-pro/redux/side-panel';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import ListPage from 'esm-web-pro/containers/pages/ListPage';
import { BooleanColumn } from 'speed-js-react-web';

import { bindActionCreators } from 'redux';
import QuickViewIcon from 'esm-web-pro/components/icons/QuickViewIcon';
import { addBreadcrumbs, removeBreadcrumb } from 'esm-web-pro/redux/breadcrumb';
import { connect, useDispatch } from 'react-redux';
import withRouter from 'speed-js-react/src/router/withRouter';
import { APIContext, withConfirmation } from 'speed-js-react';

import { useHistory } from 'react-router';
import CableModelSidePanel from './CableModelSidePanel';


const CABLE_MODEL_SIDE_PANEL_KEY = 'CABLE_MODEL_ID';


const messages = defineMessages({
    cableModels: {
        id: 'adminpage-infrastructure-cable-models',
        defaultMessage: 'Cable models',
    },
});

type Props = {
    match: Object,
    addBreadcrumbs: (title: string, link: string) => void,
    removeBreadcrumb: (title: string) => void,
    confirm: (confirmation: Confirmation) => void,
};

const CableModelDashboard = ({
    match,
    addBreadcrumbs: addBreadcrumbsAction,
    removeBreadcrumb: removeBreadcrumbAction,
    confirm,
}: Props) => {
    const listRef = React.useRef(null);
    const history = useHistory();
    const dispatch = useDispatch();

    const { apiClient } = React.useContext(APIContext);

    const { formatMessage } = useIntl();

    const [cableModel, setCableModel] = React.useState(null);

    const invokeConfirmDialog = React.useCallback((message, confirmCallback) => confirm({
        message,
        actions: {
            cancel: {
                label: <FormattedMessage id="cancel-btn" defaultMessage="Cancel" />,
                callback: () => {},
                type: 'negative',
            },
            confirm: {
                label: <FormattedMessage id="confirm-btn" defaultMessage="Confirm" />,
                callback: confirmCallback,
                type: 'positive',
            },
        },
    }), [confirm]);

    const reloadData = () => {
        setCableModel(null);

        if (listRef.current && listRef.current.table.current) {
            listRef.current.table.current.reloadData();
        }
        // We need to reload the side panel as well
        if (cableModel) {
            apiClient.resources.cableModelNomenclature.get(cableModel.id).then((response) => {
                setCableModel(response);
            });
        }
    };

    const getTitle = (obj) => {
        if (obj.translations.bg) {
            return obj.translations.bg.name;
        }
        return '';
    };

    const handleModelSelect = (row) => {
        const { original } = row;
        if (cableModel && cableModel.id === original.id) {
            // Do nothing since the user clicked on the same row
        } else if (cableModel) {
            invokeConfirmDialog(
                <FormattedMessage
                    id="cable-model-quickview-confirm"
                    defaultMessage="Are you sure you want to open a new cable model? All unsaved changes will be lost."
                />,
                () => {
                    setCableModel(original);
                    history.push(`/administration/infrastructure/cable-models/${original.id}`);
                },
            );
        } else {
            setCableModel(original);
            history.push(`/administration/infrastructure/cable-models/${original.id}`);
        }
    };

    const handleClose = React.useCallback(() => {
        closePanel(CABLE_MODEL_SIDE_PANEL_KEY, true);
        setCableModel(null);
        history.push('/administration/infrastructure/cable-models');
    }, [history]);

    const columns = [
        {
            Header: <FormattedMessage id="name" defaultMessage="Name" />,
            accessor: 'translations.bg.name',
            orderBy: 'name',
            minWidth: 120,
        },
        {
            Header: <FormattedMessage id="nomenclatureitem-code" defaultMessage="Code" />,
            accessor: 'code',
            minWidth: 120,
        },
        {
            Header: <FormattedMessage id="nomenclatureitem-is-active" defaultMessage="Is active" />,
            accessor: 'is_active',
            minWidth: 70,
            Cell: BooleanColumn,
        },
        {
            Header: <FormattedMessage id="cable-attributes-fiber-count" defaultMessage="Fiber count" />,
            accessor: 'fibers_count',
            minWidth: 180,
        },
        {
            Header: <FormattedMessage id="cable-attributes-standard" defaultMessage="Cable standard" />,
            accessor: 'cable_standard',
            minWidth: 120,
        },
        {
            Header: <FormattedMessage id="fiber-model" defaultMessage="Fiber model" />,
            accessor: 'fiber_model',
            minWidth: 120,
        },
        {
            Header: <FormattedMessage id="tube-count" defaultMessage="Tube count" />,
            accessor: 'tube_count',
            minWidth: 80,
        },
        {
            Header: <FormattedMessage id="diameter" defaultMessage="Diameter" />,
            accessor: 'diameter',
            minWidth: 80,
        },
        {
            Header: <FormattedMessage id="bearing" defaultMessage="Bearing" />,
            accessor: 'bearing',
            Cell: BooleanColumn,
            minWidth: 120,
        },
        {
            Header: <FormattedMessage id="cable-is-armored" defaultMessage="Armed" />,
            accessor: 'armed',
            Cell: BooleanColumn,
            minWidth: 120,
        },
        {
            Header: <FormattedMessage id="cable-manufacturer" defaultMessage="Manufacturer" />,
            accessor: 'manufacturer',
            minWidth: 120,
        },
        {
            Header: <FormattedMessage id="infrastructure-code" defaultMessage="Infrastructure code" />,
            accessor: 'eit_infrastructure_code',
            minWidth: 120,
        },
        {
            Header: <FormattedMessage id="infrastructure-layer-id" defaultMessage="EIT Infrastructure layer id" />,
            accessor: 'eit_infrastructure_layer_id',
            minWidth: 80,
        },
        {
            Header: <FormattedMessage id="export-default" defaultMessage="Export default" />,
            accessor: 'eit_export_default',
            Cell: BooleanColumn,
            minWidth: 120,
        },
        {
            label: <FormattedMessage
                id="projmanag-counterparties-quickview"
                defaultMessage="Quick View"
            />,
            accessor: 'id',
            Cell: (props) => (
                <Box
                    direction="row"
                    justify="center"
                    gap="small"
                >
                    <Button onClick={() => handleModelSelect(props)} plain={false} icon={<QuickViewIcon />} />
                </Box>
            ),
            maxWidth: 70,
        },
    ];

    const additionalColumns = (cableModelFields) => {
        if (cableModelFields) {
            cableModelFields.forEach(([name, field]) => {
                if (
                    ['created', 'modified', 'is_active', 'data', 'translations', 'code'].includes(name)
                    || field.type === 'json'
                ) {
                    return;
                }
                const col = {
                    Header: field.ui.label,
                    accessor: name,
                    minWidth: 120,
                };
                if (field.type === 'boolean') {
                    col.Cell = BooleanColumn;
                } else if (field.type === 'choice' && !field.validation.many) {
                    col.Cell = ({ value }) => {
                        if (!value) {
                            return '';
                        }

                        const choice = field.ui.choices[value];
                        return choice || '';
                    };
                } else if (field.type === 'choice' && field.validation.many) {
                    col.Cell = ({ value }) => {
                        if (value && value.length) {
                            return value.map((v) => {
                                const choice = field.ui.choices[v];
                                return choice || '';
                            }).filter((v) => (!!v)).join(', ');
                        }
                        return '';
                    };
                }
                columns.push(col);
            });
        }
    };


    const loadNomenclatureFields = () => {
        apiClient.resources.cableModelNomenclature.schema().then((response) => {
            additionalColumns(response.fields);
        });
    };

    React.useEffect(() => {
        if (cableModel) {
            dispatch(addBreadcrumbsAction([
                {
                    title: formatMessage(messages.cableModels),
                    url: '/administration/infrastructure/cable-models',
                },
                {
                    title: getTitle(cableModel),
                    url: `/administration/infrastructure/cable-models/${cableModel.id}`,
                },
            ]));
        }

        return () => {
            if (cableModel) {
                dispatch(removeBreadcrumbAction(getTitle(cableModel)));
            }
        };
    }, [cableModel, addBreadcrumbsAction, removeBreadcrumbAction, dispatch, formatMessage]);

    React.useEffect(() => {
        loadNomenclatureFields();
    }, []);

    return (
        <>
            {(cableModel) && (
                <CableModelSidePanel
                    key={cableModel.id}
                    panelId={CABLE_MODEL_SIDE_PANEL_KEY}
                    object={cableModel}
                    reload={reloadData}
                    history={history}
                    onClose={handleClose}
                />
            )}

            <ListPage
                ref={listRef}
                match={match}
                resourceName="cableModelNomenclature"
                permissions={[
                    'networks.view_infrastructureprovider',
                    'networks.delete_infrastructureprovider',
                    'networks.change_infrastructureprovider',
                    'networks.add_infrastructureprovider',
                ]}
                title={formatMessage(messages.cableModels)}
                columns={columns}
                newObjectUrl="/administration/infrastructure/cable-models/new"
                newObjectLabel={(
                    <FormattedMessage
                        id="adminpage-networks-new-cable-model"
                        defaultMessage="New Cable Model"
                    />
                )}
                blockingSpinner={false}
                spinnerType="clipLoader"
            />

        </>
    );
};

const mapDispatchToProps = ((dispatch) => (bindActionCreators({ addBreadcrumbs, removeBreadcrumb }, dispatch)));
export default withRouter(connect(null, mapDispatchToProps)(withConfirmation(CableModelDashboard)));
